import { LogOut, MessageCirclePlus, Palette, Pin, Search, UserPlus, Users, Video } from "lucide-react";
import { createPortal } from "react-dom";
import { ChatGuideModal } from "@/components/ChatGuideModal";
import { ChatSwipeableConversationRow } from "@/components/ChatSwipeableConversationRow";
import { ChatThemePanel } from "@/components/ChatThemePanel";
import { AvatarBubble, ClientPill } from "@/components/chatChrome";
import {
  conversationTitle,
  formatChatListWhen,
  peerAvatarUrl,
  type ChatMobilePane,
} from "@/lib/chatFormat";
import { conversationSidebarPreview } from "@/lib/chatSidebarPreview";
import { chatSidebarKind } from "@/lib/chatSidebarOrder";
import {
  chatCallIsJoinable,
  conversationClientPillName,
  isChatPeerBusyInCall,
  normalizeChatPresence,
  type ChatColleague,
  type ChatConversation,
  type ChatPresenceStatus,
} from "@/lib/chatApi";

type Props = {
  selfId: number;
  mobilePane: ChatMobilePane;
  myLabel: string;
  myAvatar: string | null;
  myStatus: ChatPresenceStatus;
  search: string;
  onSearchChange: (value: string) => void;
  loadingList: boolean;
  filtered: ChatConversation[];
  pinnedIds: number[];
  selectedId: number | null;
  presence: Record<number, string>;
  colleagues?: ChatColleague[];
  suppressJoinCallId: string | null;
  myCallId?: string | null;
  showCreateMenu: boolean;
  showTheme: boolean;
  onLogout: () => void;
  onOpenProfile: () => void;
  onToggleCreateMenu: () => void;
  onCloseCreateMenu: () => void;
  onOpenNewDm: () => void;
  onOpenNewGroup: () => void;
  onOpenNewGuest?: () => void;
  onToggleTheme: () => void;
  onCloseTheme: () => void;
  onSelectConversation: (id: number) => void;
  onMarkUnread: (c: ChatConversation) => void;
  onRequestDelete: (c: ChatConversation) => void;
  onTogglePin: (id: number) => void;
  onOpenAvatarPreview: (url: string | null | undefined, alt?: string) => void;
};

export function ChatConversationList({
  selfId,
  mobilePane,
  myLabel,
  myAvatar,
  myStatus,
  search,
  onSearchChange,
  loadingList,
  filtered,
  pinnedIds,
  selectedId,
  presence,
  colleagues = [],
  suppressJoinCallId,
  myCallId = null,
  showCreateMenu,
  showTheme,
  onLogout,
  onOpenProfile,
  onToggleCreateMenu,
  onCloseCreateMenu,
  onOpenNewDm,
  onOpenNewGroup,
  onOpenNewGuest,
  onToggleTheme,
  onCloseTheme,
  onSelectConversation,
  onMarkUnread,
  onRequestDelete,
  onTogglePin,
  onOpenAvatarPreview,
}: Props) {
  const pinnedSet = new Set(pinnedIds);
  const pinned = pinnedIds
    .map((id) => filtered.find((c) => c.id === id))
    .filter((c): c is ChatConversation => Boolean(c));
  const rest = filtered.filter((c) => !pinnedSet.has(c.id));
  const dms = rest.filter((c) => chatSidebarKind(c) === "dm");
  const groups = rest.filter((c) => chatSidebarKind(c) === "group");
  const rooms = rest.filter((c) => chatSidebarKind(c) === "room");

  const renderRow = (c: ChatConversation) => {
    const title = conversationTitle(c, selfId);
    const active = c.id === selectedId;
    const isPinned = pinnedSet.has(c.id);
    const peerId = c.type === "dm" ? c.member_ids.map((id) => Number(id)).find((id) => id !== selfId) : undefined;
    const colleaguePresence =
      peerId != null ? colleagues.find((x) => Number(x.id) === peerId)?.presence : undefined;
    const peerPresence =
      peerId != null
        ? isChatPeerBusyInCall(peerId, presence, colleaguePresence)
          ? "incall"
          : normalizeChatPresence(presence[peerId] ?? colleaguePresence)
        : "offline";
    const peerInCall = isChatPeerBusyInCall(peerId, presence, colleaguePresence);
    const thisConvLive =
      !!c.active_call && c.active_call.call_id !== suppressJoinCallId;
    const iAmInThisCall =
      myCallId != null && c.active_call?.call_id === myCallId;
    const liveJoinableHere = thisConvLive && chatCallIsJoinable(c.active_call);
    const showJoin = liveJoinableHere && !iAmInThisCall;
    const callBusy =
      (c.type === "dm" && peerInCall && !iAmInThisCall && !liveJoinableHere) ||
      (c.type === "group" && thisConvLive && !liveJoinableHere && !iAmInThisCall);
    const avatar = peerAvatarUrl(c, selfId);
    const clientPill = conversationClientPillName(c);
    const unread = (c.unread_count ?? 0) > 0;
    const preview = conversationSidebarPreview(c, selfId, colleagues);
    return (
      <ChatSwipeableConversationRow
        key={c.id}
        convId={c.id}
        active={active}
        onSelect={() => onSelectConversation(c.id)}
        onMarkUnread={() => onMarkUnread(c)}
        onDelete={() => onRequestDelete(c)}
      >
        <AvatarBubble
          label={title}
          url={avatar}
          presence={peerPresence}
          onClick={avatar ? () => onOpenAvatarPreview(avatar, title) : undefined}
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1.5">
              {isPinned ? (
                <Pin className="size-3 shrink-0 text-[var(--chat-accent-strong)]" aria-hidden />
              ) : null}
              <span className={`truncate text-sm ${unread ? "font-bold" : "font-medium"}`}>{title}</span>
              {clientPill ? <ClientPill name={clientPill} /> : null}
              {showJoin ? (
                <span
                  className="chat-list-call-live"
                  title="Visio en cours"
                  aria-label="Visio en cours"
                >
                  <Video className="size-3" />
                </span>
              ) : iAmInThisCall ? (
                <span
                  className="chat-list-incall-dot"
                  title="Vous êtes en visio"
                  aria-label="Vous êtes en visio"
                >
                  <Video className="size-3" strokeWidth={2.5} />
                </span>
              ) : peerInCall || callBusy ? (
                <span
                  className="chat-list-incall-dot"
                  title="En visio — appel indisponible"
                  aria-label="En visio"
                >
                  <Video className="size-3" strokeWidth={2.5} />
                </span>
              ) : null}
            </span>
            <span className="flex shrink-0 items-center gap-1">
              <span className="text-[10px] text-[var(--chat-muted)]">
                {formatChatListWhen(c.last_message?.created_at ?? c.updated_at)}
              </span>
              <button
                type="button"
                data-chat-pin
                className={`chat-list-pin${isPinned ? " is-on" : ""}`}
                title={isPinned ? "Désépingler" : "Épingler en haut"}
                aria-label={isPinned ? "Désépingler" : "Épingler en haut"}
                aria-pressed={isPinned}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onTogglePin(c.id);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onTogglePin(c.id);
                }}
              >
                <Pin className="size-3.5" />
              </button>
            </span>
          </span>
          <span className="mt-0.5 flex min-w-0 items-center justify-between gap-2">
            <span
              className={`min-w-0 truncate text-xs ${
                unread ? "font-semibold text-[var(--chat-text)]" : "text-[var(--chat-muted)]"
              }`}
            >
              {preview.text}
            </span>
            {unread ? (
              <span className="chat-list-unread-dot" title="Nouveau" aria-label="Nouveau" />
            ) : null}
          </span>
        </span>
      </ChatSwipeableConversationRow>
    );
  };

  const firstClass = (before: boolean) => (before ? "" : " chat-list-section--first");

  return (
    <aside
      className={`chat-panel chat-mobile-pane chat-mobile-pane--list relative flex w-full max-w-md flex-col border-r md:w-[360px] md:max-w-[360px] md:shrink-0 md:overflow-hidden ${
        mobilePane === "thread" ? "is-away" : ""
      }`}
    >
      <header className="flex items-center gap-2 border-b border-[var(--chat-border)] px-3 py-3">
        <AvatarBubble
          label={myLabel}
          url={myAvatar}
          presence={myStatus}
          size="md"
          title="Mon profil (statut, son, QR)"
          onClick={onOpenProfile}
        />
        <div className="flex min-w-0 flex-1 items-center gap-0.5">
          <h1 className="min-w-0 truncate text-lg font-semibold">
            <span className="text-[0.7em] font-medium opacity-70">Aurélia</span> Chat
          </h1>
          <ChatGuideModal />
        </div>
        <div className="relative">
          <button
            type="button"
            title="Créer"
            className="rounded-lg p-2 text-[var(--chat-accent-strong)] hover:bg-[var(--chat-accent-soft)]"
            aria-expanded={showCreateMenu}
            aria-haspopup="menu"
            onClick={onToggleCreateMenu}
          >
            <MessageCirclePlus className="size-5" />
          </button>
          {showCreateMenu ? (
            <>
              <button
                type="button"
                className="fixed inset-0 z-30 cursor-default"
                aria-label="Fermer le menu"
                onClick={onCloseCreateMenu}
              />
              <div
                className="chat-create-menu absolute right-0 top-[calc(100%+0.35rem)] z-40 min-w-[11.5rem] overflow-hidden rounded-xl border border-[var(--chat-border)] bg-[var(--chat-surface)] py-1 shadow-[var(--chat-shadow)]"
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[var(--chat-text)] hover:bg-[var(--chat-surface-elevated)]"
                  onClick={onOpenNewDm}
                >
                  <MessageCirclePlus className="size-4 text-[var(--chat-accent-strong)]" />
                  Contact
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[var(--chat-text)] hover:bg-[var(--chat-surface-elevated)]"
                  onClick={onOpenNewGroup}
                >
                  <Users className="size-4 text-[var(--chat-accent-strong)]" />
                  Groupe
                </button>
                {onOpenNewGuest ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[var(--chat-text)] hover:bg-[var(--chat-surface-elevated)]"
                    onClick={onOpenNewGuest}
                  >
                    <UserPlus className="size-4 text-[var(--chat-accent-strong)]" />
                    Invité
                  </button>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
        <button
          type="button"
          title="Thème"
          className="relative rounded-lg p-2 text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)]"
          onClick={onToggleTheme}
        >
          <Palette className="size-5" />
        </button>
        <button
          type="button"
          title="Déconnexion"
          className="rounded-lg p-2 text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)] hover:text-[var(--chat-danger)]"
          onClick={onLogout}
        >
          <LogOut className="size-5" />
        </button>
      </header>

      {showTheme
        ? createPortal(
            <div
              className="chat-app-sheet-overlay"
              onClick={onCloseTheme}
              role="presentation"
            >
              <div
                className="chat-panel chat-app-sheet"
                role="dialog"
                aria-label="Apparence et son"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="chat-app-sheet__handle" aria-hidden />
                <ChatThemePanel onClose={onCloseTheme} />
              </div>
            </div>,
            document.body
          )
        : null}

      <div className="px-3 py-2">
        <label className="flex items-center gap-2 rounded-xl bg-[var(--chat-bg)] px-3 py-2">
          <Search className="size-4 text-[var(--chat-muted)]" />
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Rechercher…"
            className="w-full bg-transparent text-sm outline-none placeholder:text-[var(--chat-muted)]"
          />
        </label>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {loadingList ? (
          <p className="p-4 text-sm text-[var(--chat-muted)]">Chargement…</p>
        ) : (
          <>
            {filtered.length === 0 ? (
              <p className="p-4 text-sm text-[var(--chat-muted)]">Aucune conversation.</p>
            ) : (
              <>
                {pinned.length > 0 ? (
                  <div className="chat-list-section chat-list-section--first">
                    <p className="chat-list-section__label">Épinglés</p>
                    {pinned.map(renderRow)}
                  </div>
                ) : null}
                {dms.length > 0 ? (
                  <div className={`chat-list-section${firstClass(pinned.length > 0)}`}>
                    <p className="chat-list-section__label">Contacts</p>
                    {dms.map(renderRow)}
                  </div>
                ) : null}
                {groups.length > 0 ? (
                  <div className={`chat-list-section${firstClass(pinned.length > 0 || dms.length > 0)}`}>
                    <p className="chat-list-section__label">Groupes</p>
                    {groups.map(renderRow)}
                  </div>
                ) : null}
                {rooms.length > 0 ? (
                  <div
                    className={`chat-list-section${firstClass(pinned.length > 0 || dms.length > 0 || groups.length > 0)}`}
                  >
                    <p className="chat-list-section__label">Salles</p>
                    {rooms.map(renderRow)}
                  </div>
                ) : null}
              </>
            )}
          </>
        )}
      </div>

      <footer className="border-t border-[var(--chat-border)] px-3 py-2.5">
        <p className="text-center text-[10px] leading-snug text-[var(--chat-muted)]">
          © 2026 Aurélia Suite. Tous droits réservés
          <span className="mx-1 opacity-50" aria-hidden>
            •
          </span>
          Conforme RGPD
          <span className="mx-1 opacity-50" aria-hidden>
            •
          </span>
          Studio Seth — Pacy-sur-Eure (27)
        </p>
      </footer>
    </aside>
  );
}
